import { Alert, Link, Text } from "@merge-api/merge-javascript-shared";
import { IntegrationSetupStepType } from "../../../config/types";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import NextStepButton from "../../shared/NextStepButton";
import { Inputs } from "./MainConnectorSetupStepBase";
import PoweredByMerge from "../../shared/PoweredByMerge";
import { getIsOauthStepType } from "./Helpers";
import useIntegrationSetupContext from "./useIntegrationSetupContext";


const getButtonText = (
  currentStepType: IntegrationSetupStepType,
  translate: (keyString: string, fallbackString: string) => string,
) => {
  switch (currentStepType) {
    case IntegrationSetupStepType.STEP_TYPE_ORG_CONSENT:
      return translate("consent-sentence", "I have provided consent");
    case IntegrationSetupStepType.STEP_TYPE_OAUTH:
    case IntegrationSetupStepType.STEP_TYPE_TRELLO_OAUTH1:
      return translate("open-window", "Open window");
    default:
      return translate("next-button", "Next");
  }
};

type Props = {
  inputs?: Inputs;
  currentStepType?: IntegrationSetupStepType;
  isStepComplete: boolean;
  allowContinueWithErrors?: boolean;
  navigateToNextStep: () => void;
  buttonTextOverride?: string;
  stepHelpGuideURL?: string;
  setIsStepComplete?: (isStepComplete: boolean) => void;
  confirmMessage?: string;
  shouldShowHelpCenterButton?: boolean;
};

const ConnectorSetupStepForm = ({
  currentStepType,
  inputs,
  isStepComplete,
  allowContinueWithErrors = false,
  navigateToNextStep,
  buttonTextOverride,
  stepHelpGuideURL,
  shouldShowHelpCenterButton,
}: Props) => {
  const { hasThirdPartyCookiesDisabled } =
    useIntegrationSetupContext();
  const { translate } = useAgentHandlerLinkModalContext();

  const hasInputErrors = Object.values(inputs ?? []).reduce(
    (acc, curr) => acc || !!curr.error,
    false
  );

  const isOauthStep = getIsOauthStepType(currentStepType);
  const hasOauthCookieEnabled = !isOauthStep || hasThirdPartyCookiesDisabled === false;

  const isHelpButtonVisible = !!(stepHelpGuideURL && shouldShowHelpCenterButton);

  return (
    <div className="flex flex-col gap-y-3 p-5">
      {!hasOauthCookieEnabled && (
        <Alert
          color="red"
          title={translate("third-party-cookies-disabled", "Enable third-party cookies")}
          showWarningIcon
          size="sm"
          className="mb-2"
        >
          <Text variant="sm" as="div" className="flex flex-col gap-2">
            {translate(
              "third-party-cookies-disabled-message",
              "To securely authenticate you must enable third-party cookies in your browser. These are set for security reasons and never used for tracking or advertising."
            )}
            <Link
              href="https://help.merge.dev/en/articles/9660563-enable-third-party-cookies-in-your-browser"
              target="_blank"
            >
              How do I enable third-party cookies in my browser?
            </Link>
          </Text>
        </Alert>
      )}

      <NextStepButton
        inverted={isHelpButtonVisible}
        disabled={
          !isStepComplete || (hasInputErrors && !allowContinueWithErrors) || !hasOauthCookieEnabled
        }
        onClick={navigateToNextStep}
      >
        {buttonTextOverride ||
          (currentStepType && getButtonText(currentStepType, translate))}
      </NextStepButton>

      {isOauthStep && <PoweredByMerge />}
    </div>
  );
};

export default ConnectorSetupStepForm;
